/*
  global.js: shared runtime for the Sauna Safe Case theme. Owner: SYSTEM. ES module, every page.
  Import it from other modules with the import map name:  import { bus, cart } from '@theme/global';
  Also exposed as window.Theme for non-module code. API and event payloads: CONTRACT.md (section 5, E3.4).
  On window load it adds html.is-loaded and emits theme:loaded (replayable), so textures, loops and other
  decorative media start only after the LCP. Budget: about 5.5 KB minified. No dependencies.
*/
const d = document;
const root = d.documentElement;

function readConfig() {
  try {
    return JSON.parse(d.getElementById('theme-config').textContent);
  } catch (e) {
    return {};
  }
}
export const config = readConfig();
export const strings = config.strings || {};
const routes = config.routes || {};
const r = (key, fallback) => (routes[key] || fallback).replace(/\/$/, '');

/* ---------- Event bus: CustomEvents on document. Last payload kept for replay. ---------- */
const last = new Map();
export const bus = {
  emit(name, detail = {}) {
    if (name === 'code:removed') last.delete('code:applied');
    last.set(name, detail);
    d.dispatchEvent(new CustomEvent(name, { detail }));
  },
  on(name, fn, { replay = false, signal } = {}) {
    const handler = (e) => fn(e.detail, e);
    d.addEventListener(name, handler, signal ? { signal } : undefined);
    if (replay && last.has(name)) fn(last.get(name));
    return () => d.removeEventListener(name, handler);
  },
  last: (name) => last.get(name),
};

/* ---------- Money ---------- */
const group = (n, dec, th, ds) => {
  const [i, f] = (n / 100).toFixed(dec).split('.');
  return i.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (f ? ds + f : '');
};
export function formatMoney(cents, format = config.moneyFormat || '${{amount}}') {
  const n = Number(cents) || 0;
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
    if (k === 'amount_no_decimals') return group(n, 0, ',', '.');
    if (k === 'amount_with_comma_separator') return group(n, 2, '.', ',');
    if (k === 'amount_no_decimals_with_comma_separator') return group(n, 0, '.', ',');
    if (k === 'amount_with_apostrophe_separator') return group(n, 2, "'", '.');
    return group(n, 2, ',', '.');
  });
}
/* Price after a percentage discount, rounded the way Shopify does it: the discount is floored to the cent per unit. */
export const codePrice = (cents, pct) => cents - Math.floor((cents * pct) / 100);

/* ---------- Discount code rules (mirror of admin; settings > Discount codes) ---------- */
function parseRules(raw) {
  const out = {};
  String(raw || '').split(/[\n;]+/).forEach((line) => {
    const [code, pct, handles = ''] = line.split(':').map((s) => s.trim());
    if (code && pct) {
      out[code.toUpperCase()] = {
        code: code.toUpperCase(),
        pct: parseFloat(pct) || 0,
        handles: handles.split(',').map((s) => s.trim()).filter(Boolean),
      };
    }
  });
  return out;
}
function parseAliases(raw) {
  const out = {};
  String(raw || '').split(/[\n,;]+/).forEach((pair) => {
    const [from, to] = pair.split(':').map((s) => s.trim().toUpperCase());
    if (from && to) out[from] = to;
  });
  return out;
}
const cc = config.codes || {};
export const codes = {
  rules: parseRules(cc.rules),
  aliases: parseAliases(cc.aliases),
  packMode: cc.packMode || 'singles',
  params: String(cc.params || 'code,discount').split(',').map((s) => s.trim()).filter(Boolean),
  caseHandles: (config.caseHandles || []).filter(Boolean),
  rule: (code) => codes.rules[String(code || '').toUpperCase()] || null,
  alias: (code) => codes.aliases[String(code || '').toUpperCase()] || null,
  appliesTo: (code, handle) => {
    const rule = codes.rule(code);
    return !!rule && rule.handles.includes(handle);
  },
};

/* ---------- Cart API (serialized; emits cart:updated / cart:error) ---------- */
let tail = Promise.resolve();
function enqueue(task) {
  const run = tail.then(task, task);
  tail = run.catch(() => {});
  return run;
}
const sectionIds = new Set();

async function request(url, body) {
  const res = await fetch(url, {
    method: body ? 'POST' : 'GET',
    credentials: 'same-origin',
    headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try {
    data = await res.json();
  } catch (e) {
    /* empty body */
  }
  if (!res.ok) {
    const err = new Error((data && (data.description || data.message)) || strings.errorGeneric || 'Error');
    err.status = res.status;
    err.retryAfter = res.headers.get('Retry-After');
    err.data = data;
    throw err;
  }
  return data;
}
function withSections(body, opts) {
  const ids = (opts.sections || [...sectionIds]).slice(0, 5);
  if (ids.length) {
    body.sections = ids.join(',');
    body.sections_url = opts.sectionsUrl || location.pathname;
  }
  return body;
}
function cartLabel(n) {
  return ((n === 1 ? strings.cartOne : strings.cartOther) || 'Cart').replace('[count]', n);
}
export function updateCartCount(n) {
  d.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = n;
    el.hidden = !n;
  });
  d.querySelectorAll('[data-cart-trigger]').forEach((el) => el.setAttribute('aria-label', cartLabel(n)));
}
function settle(c, sections, action, opts, items) {
  updateCartCount(c.item_count);
  const detail = { cart: c, sections: sections || null, action: opts.action || action, source: opts.source || 'unknown' };
  if (items) detail.items = items;
  bus.emit('cart:updated', detail);
  return detail;
}
/* One serialized mutation: POST, (re)read the cart when the endpoint does not return it, emit, return detail. */
function mutate(key, path, body, opts, action) {
  return enqueue(async () => {
    try {
      const res = await request(r(key, path) + '.js', withSections(body, opts));
      const c = action === 'add' ? await request(r('cart', '/cart') + '.js') : res;
      return settle(c, res.sections, action, opts, action === 'add' ? res.items : null);
    } catch (err) {
      bus.emit('cart:error', { message: err.message, status: err.status || 0, source: opts.source || 'unknown', action });
      throw err;
    }
  });
}

export const cart = {
  registerSection: (id) => id && sectionIds.add(id),
  unregisterSection: (id) => sectionIds.delete(id),
  get: () => request(r('cart', '/cart') + '.js'),
  /* items: [{ id, quantity, properties? }] (case + add-on in ONE request). */
  add: (items, opts = {}) => mutate('cartAdd', '/cart/add', { items }, opts, 'add'),
  /* body: { id | line, quantity, properties? } */
  change: (body, opts = {}) => mutate('cartChange', '/cart/change', { ...body }, opts, 'change'),
  /* body: { updates?, discount?, attributes?, note? }. `discount` REPLACES the whole code list. */
  update: (body, opts = {}) => mutate('cartUpdate', '/cart/update', { ...body }, opts, body.discount !== undefined ? 'discount' : 'update'),
  /* Sets the full code list (dedupes, uppercases). Pass [] to remove all codes. */
  applyCodes: (list, opts = {}) =>
    cart.update({ discount: [...new Set(list.map((x) => String(x).trim().toUpperCase()).filter(Boolean))].join(',') }, opts),
  codesOf: (c) => ((c && c.discount_codes) || []).map((x) => String(x.code).toUpperCase()),
};

/* ---------- DOM helpers ---------- */
export function parseHTML(html) {
  return new DOMParser().parseFromString(html, 'text/html');
}
/* Replace the element matching `selector` in the page with the one from an HTML string (e.g. a rendered section). */
export function swapFromHTML(html, selector, scope = d) {
  const next = parseHTML(html).querySelector(selector);
  const current = scope.querySelector(selector);
  if (next && current) current.replaceWith(next);
  return next;
}
export const onIdle = (fn) => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 2000 }) : setTimeout(fn, 200));
export function debounce(fn, ms = 200) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}
export const prefersReducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
/* True when the visitor asked for reduced data (Save-Data / Lite mode). Decorative video never mounts then. */
export const saveData = () => !!(navigator.connection && navigator.connection.saveData);

/* ---------- Scroll lock (pair with native <dialog>.showModal(), which traps focus and handles Esc) ---------- */
export const lockScroll = () => root.classList.add('is-scroll-locked');
export const unlockScroll = () => root.classList.remove('is-scroll-locked');

/* ---------- <details data-auto-close>: Esc and outside click close it (header menu, popovers) ---------- */
d.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  d.querySelectorAll('details[data-auto-close][open]').forEach((el) => {
    el.removeAttribute('open');
    el.querySelector('summary').focus();
  });
});
d.addEventListener('click', (e) => {
  d.querySelectorAll('details[data-auto-close][open]').forEach((el) => {
    if (!el.contains(e.target)) el.removeAttribute('open');
  });
});

/* ---------- Boot ---------- */
root.classList.remove('no-js');
root.classList.add('js');

window.Theme = Object.assign(window.Theme || {}, { config, bus, cart, codes, formatMoney, codePrice });
bus.emit('theme:ready', { config });

/* After window load: html.is-loaded (CSS gates textures and grain on it) + theme:loaded (subscribe with replay). */
function loaded() {
  root.classList.add('is-loaded');
  bus.emit('theme:loaded', {});
}
if (d.readyState === 'complete') loaded();
else window.addEventListener('load', loaded, { once: true });
